/**
 * Inline "follow this venue / series" chip shown on the venue line of event
 * rows and cards. It is the explicit, labeled counterpart to the event 👍 Save
 * button — a filled ★ means "you follow this venue", which is *why* that venue's
 * events are surfaced. Keeping the venue-follow control visually distinct (a
 * gold star chip on the venue line) from the event Save control (a 👍 in the
 * action bar) resolves the old ambiguity where a saved event and an event at a
 * followed venue looked like the same gold outline.
 *
 *  - Following → a labeled gold chip: ★ "Favorite venue" (click to unfollow).
 *  - Not following → a subtle outline-star toggle (click to follow).
 *
 * Hidden for anonymous users. Reuses the generic favorites store (kind =
 * `venue` | `series`) so following here is identical to following from the
 * venue/series pages.
 */
import { useIsAuthenticated } from '@azure/msal-react';
import { useFavorites } from '../lib/favorites';

interface Props {
  kind: 'venue' | 'series';
  targetId: string;
  /** User-visible name — used in the tooltip + aria-label. */
  targetName: string;
  /** Stop clicks from bubbling into a clickable row/card. Default true. */
  stopPropagation?: boolean;
  /** Show a text label in the *not-following* state too (for roomy surfaces
   *  like cards, the detail modal and map popups). Dense compact list rows
   *  leave this off and stay icon-only. Default false. */
  offLabel?: boolean;
  /** Extra classes for the button. */
  className?: string;
}

export default function EntityFollowChip({
  kind,
  targetId,
  targetName,
  stopPropagation = true,
  offLabel = false,
  className
}: Props) {
  const isAuthed = useIsAuthenticated();
  const { isFavorite, toggle, ready } = useFavorites();
  if (!isAuthed || !targetId) return null;

  const following = isFavorite(kind, targetId);
  const noun = kind === 'venue' ? 'venue' : 'series';
  const cls =
    `mh-follow-chip mh-follow-chip--${following ? 'on' : 'off'}${className ? ` ${className}` : ''}`;

  return (
    <button
      type="button"
      className={cls}
      aria-pressed={following}
      aria-label={following ? `Unfollow ${noun} ${targetName}` : `Follow ${noun} ${targetName}`}
      title={
        following
          ? `You follow this ${noun} — its events appear under “Favorites only”. Click to unfollow.`
          : `Follow this ${noun} so its events appear under “Favorites only”.`
      }
      disabled={!ready}
      onClick={(e) => {
        if (stopPropagation) {
          e.stopPropagation();
          e.preventDefault();
        }
        void toggle(kind, targetId);
      }}
    >
      <i className={`bi ${following ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
      {following ? (
        <span className="mh-follow-chip__label">Favorite {noun}</span>
      ) : (
        offLabel && <span className="mh-follow-chip__label">Follow {noun}</span>
      )}
    </button>
  );
}
